import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, setAuthToken } from "../lib/api";
import { Bell, Menu, X, LogOut, Coins, LifeBuoy } from "lucide-react";
import { ClinqLogo } from "./ClinqLogo";
import UploadProcessingIndicator from "./founder/UploadProcessingIndicator";

function CreditsChip({ credits, onClick, directPath }) {
  if (!credits) return null;
  if (directPath && !credits.report_unlocked) return null; // don't push the report on direct-path founders
  return (
    <button
      data-testid="founder-credits-chip"
      onClick={onClick}
      title={credits.report_unlocked
        ? `${credits.balance} re-run credit(s) remaining`
        : "Unlock your detailed readiness report"}
      className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-900 hover:bg-amber-100"
    >
      <Coins className="h-3.5 w-3.5"/>
      {credits.report_unlocked
        ? <>{credits.balance} <span className="hidden sm:inline">credit{credits.balance === 1 ? "" : "s"}</span></>
        : <span>Get my report</span>}
    </button>
  );
}

function BellButton({ unread, onClick }) {
  return (
    <button
      data-testid="alerts-bell"
      onClick={onClick}
      aria-label="Alerts"
      className="relative rounded-lg border border-neutral-200 bg-white p-2 text-neutral-600 hover:bg-neutral-50"
    >
      <Bell className="h-4 w-4"/>
      {unread > 0 && (
        <span data-testid="alerts-unread-badge"
          className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
          {unread > 9 ? "9+" : unread}
        </span>
      )}
    </button>
  );
}

function DesktopNav({ nav, isActive, onNavigate }) {
  return (
    <nav className="hidden flex-1 items-center justify-center gap-x-5 gap-y-2 md:flex lg:gap-7" aria-label="Primary">
      {nav?.map(n => (
        <button
          key={n.to}
          onClick={() => onNavigate(n.to)}
          data-testid={`shell-nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
          className={"whitespace-nowrap text-sm font-medium " +
            (isActive(n.to) ? "text-[#0A8B85]" : "text-neutral-600 hover:text-neutral-900")}
        >
          {n.label}
        </button>
      ))}
    </nav>
  );
}

function MobileDrawer({ open, onClose, nav, isActive, onNavigate, user, logout }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 md:hidden" data-testid="shell-mobile-drawer">
      <button
        type="button"
        aria-label="Close menu"
        data-testid="shell-mobile-drawer-overlay"
        className="absolute inset-0 bg-black/55"
        onClick={onClose}
      />
      <aside className="absolute left-0 top-0 flex h-full w-80 max-w-[85vw] flex-col gap-1 bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between pb-3">
          <ClinqLogo size="md" tone="teal" />
          <button
            onClick={onClose}
            aria-label="Close menu"
            data-testid="shell-mobile-drawer-close"
            className="rounded-lg p-2 text-neutral-500 hover:bg-neutral-100"
          >
            <X className="h-4 w-4"/>
          </button>
        </div>
        <div className="mb-1 truncate border-b border-neutral-100 pb-3 text-xs text-neutral-500">
          {user?.email}
        </div>
        <nav className="mt-3 flex flex-col gap-1" aria-label="Mobile primary">
          {nav?.map(n => (
            <button
              key={n.to}
              onClick={() => onNavigate(n.to)}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
              className={"rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-colors " +
                (isActive(n.to)
                  ? "bg-[#0A8B85]/10 text-[#0A8B85]"
                  : "text-neutral-700 hover:bg-neutral-100")}
            >
              {n.label}
            </button>
          ))}
        </nav>
        <button
          onClick={logout}
          data-testid="shell-mobile-logout"
          className="mt-auto flex items-center justify-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"
        >
          <LogOut className="h-4 w-4"/> Log out
        </button>
      </aside>
    </div>
  );
}

function useShellPolling(user) {
  const [unread, setUnread] = useState(0);
  const [credits, setCredits] = useState(null);
  const [supportUnread, setSupportUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    if (user.role !== "funder" && user.role !== "founder") return;
    let mounted = true;
    const fetchCount = async () => {
      try {
        const url = user.role === "funder"
          ? "/funder/inbox"
          : "/companies/me/join-requests/unread-count";
        const { data } = await api.get(url);
        if (mounted) setUnread(user.role === "funder" ? (data.unread || 0) : (data.count || 0));
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.warn("[AppShell] unread-count fetch failed", err);
      }
    };
    const fetchSupportUnread = async () => {
      if (user.role !== "founder") return;
      try {
        const { data } = await api.get("/founder/support-tickets/unread-count");
        if (mounted) setSupportUnread(data.count || 0);
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.warn("[AppShell] support unread fetch failed", err);
      }
    };
    const fetchCredits = async () => {
      if (user.role !== "founder") return;
      try {
        const { data } = await api.get("/founder/credits/balance");
        if (mounted) setCredits(data);
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.warn("[AppShell] credits fetch failed", err);
      }
    };
    fetchCount();
    fetchCredits();
    fetchSupportUnread();
    const t = setInterval(() => { fetchCount(); fetchCredits(); fetchSupportUnread(); }, 30000);
    // Listen for explicit refresh nudges from anywhere in the app (e.g. when a
    // re-run completes and the credit balance must reflect immediately in the
    // header chip instead of waiting up to 30s for the next poll).
    const onNudge = () => { fetchCredits(); fetchCount(); };
    const onTicketsRead = () => fetchSupportUnread();
    window.addEventListener("clinq:credits-changed", onNudge);
    window.addEventListener("clinq:tickets-read", onTicketsRead);
    return () => {
      mounted = false;
      clearInterval(t);
      window.removeEventListener("clinq:credits-changed", onNudge);
      window.removeEventListener("clinq:tickets-read", onTicketsRead);
    };
  }, [user]);

  return { unread, credits, supportUnread };
}

// Purple banner shown for the whole acting-on-behalf session, with a
// one-click switcher between the advisor's confirmed clients.
function ActingAdvisorBanner({ user }) {
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState(null);
  const [switching, setSwitching] = useState(null);

  useEffect(() => {
    if (!open || clients !== null) return;
    api.get("/advisors/acting/clients")
      .then((r) => setClients(r.data?.clients || []))
      .catch(() => setClients([]));
  }, [open, clients]);

  const exit = async () => {
    try {
      const { data } = await api.post("/advisors/exit-client");
      if (data?.token) setAuthToken(data.token);
      window.location.href = "/advisor/dashboard";
    } catch (_e) { /* stay put */ }
  };

  const switchTo = async (c) => {
    setSwitching(c.link_id);
    try {
      const { data } = await api.post(`/advisors/acting/switch/${c.link_id}`);
      if (data?.token) setAuthToken(data.token);
      window.location.href = "/founder/dashboard";
    } catch (_e) {
      setSwitching(null);
    }
  };

  const others = (clients || []).filter((c) => !c.current);
  return (
    <div className="sticky top-0 z-40 bg-[#4C1D95] px-4 py-2 text-xs font-semibold text-white"
         data-testid="acting-advisor-banner">
      <div className="relative mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
        <span>
          You are currently managing <b>{user.managed_business_name || user.name}</b> on behalf of <b>{user.name}</b>.
        </span>
        <div className="relative">
          <button
            type="button"
            data-testid="acting-advisor-switch"
            onClick={() => setOpen((o) => !o)}
            className="rounded-full border border-white/40 px-3 py-0.5 text-[11px] font-bold hover:bg-white/10"
          >
            Switch client ▾
          </button>
          {open && (
            <div className="absolute left-1/2 top-full z-50 mt-1.5 w-56 -translate-x-1/2 rounded-xl border border-neutral-200 bg-white py-1 text-left shadow-xl"
                 data-testid="acting-switch-menu">
              {clients === null ? (
                <p className="px-3 py-2 text-[11px] text-neutral-500">Loading clients…</p>
              ) : others.length === 0 ? (
                <p className="px-3 py-2 text-[11px] text-neutral-500">No other confirmed clients yet</p>
              ) : others.map((c, i) => (
                <button key={c.link_id} type="button"
                  data-testid={`acting-switch-option-${i}`}
                  disabled={!!switching}
                  onClick={() => switchTo(c)}
                  className="block w-full truncate px-3 py-2 text-left text-xs font-semibold text-neutral-800 hover:bg-[#F4F8F8] disabled:opacity-50">
                  {switching === c.link_id ? "Switching…" : c.client_name}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          data-testid="acting-advisor-exit"
          onClick={exit}
          className="rounded-full border border-white/40 px-3 py-0.5 text-[11px] font-bold hover:bg-white/10"
        >
          Back to My Dashboard
        </button>
      </div>
    </div>
  );
}

export default function AppShell({ children, nav: navProp }) {  const { user, logout } = useAuth();
  // Business founders get ONE consistent nav regardless of page: Dashboard, Funder matches,
  // Readiness Report, Team, Support — plus any page-specific extras that aren't duplicates.
  const FOUNDER_NAV = [
    { to: "/founder/dashboard", label: "Dashboard" },
    { to: "/founder/apply", label: "Funder matches" },
    { to: "/onboarding", label: "Readiness Report" },
    { to: "/founder/team", label: "Team" },
    { to: "/founder/support", label: "Support" },
  ];
  const nav = (user?.role === "founder" && user?.company_kind !== "project" && navProp)
    ? [...FOUNDER_NAV, ...navProp.filter((n) => !FOUNDER_NAV.some((f) => f.to === n.to) && n.to !== "/founder/tutorial")]
    : navProp;
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { unread, credits, supportUnread } = useShellPolling(user);

  // Close drawer whenever the route changes
  useEffect(() => { setOpen(false); }, [location.pathname]);
  // Founder clicked a team-invite link before signing up — take them back to it once.
  useEffect(() => {
    const token = localStorage.getItem("pending_team_invite");
    if (token && user?.role === "founder") {
      localStorage.removeItem("pending_team_invite");
      navigate(`/join-team/${token}`);
    }
  }, [user, navigate]);
  // Lock body scroll when drawer is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const bellTarget = user?.role === "funder" ? "/funder/inbox" : "/founder/team";
  const isActive = (to) => location.pathname === to;
  const showCreditsChip = user?.role === "founder";
  const showBell = user?.role === "funder" || user?.role === "founder";

  return (
    <div className="min-h-screen bg-[#F4F8F8]" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Advisor acting-on-behalf banner — persists across the whole session */}
      {user?.acting_advisor && <ActingAdvisorBanner user={user} />}
      <header className="sticky top-0 z-30 border-b border-neutral-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="shell-menu-toggle"
              aria-label="Open menu"
              aria-expanded={open}
              onClick={() => setOpen(true)}
              className="rounded-lg border border-neutral-200 bg-white p-2 text-neutral-700 hover:bg-neutral-50 md:hidden"
            >
              <Menu className="h-4 w-4" />
            </button>
            <Link to="/" data-testid="shell-logo">
              <ClinqLogo size="md" tone="teal" testid="shell-logo-mark" />
            </Link>
          </div>

          <DesktopNav nav={nav} isActive={isActive} onNavigate={navigate} />

          {/* Right cluster */}
          <div className="flex items-center gap-2 sm:gap-3">
            <UploadProcessingIndicator userRole={user?.role} />
            {showCreditsChip && <CreditsChip credits={credits} directPath={user?.pathway === "direct"} onClick={() => navigate("/onboarding")} />}
            {user?.role === "founder" && (
              <button
                type="button"
                aria-label="Support tickets"
                data-testid="shell-support-btn"
                onClick={() => navigate("/founder/support")}
                className="relative rounded-lg border border-neutral-200 bg-white p-2 text-neutral-600 hover:bg-neutral-50"
              >
                <LifeBuoy className="h-4 w-4" />
                {supportUnread > 0 && (
                  <span data-testid="support-unread-badge"
                    className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#F59E0B] px-1 text-[9px] font-extrabold text-white">
                    {supportUnread > 9 ? "9+" : supportUnread}
                  </span>
                )}
              </button>
            )}
            {showBell && <BellButton unread={unread} onClick={() => navigate(bellTarget)} />}
            <span className="hidden max-w-[160px] truncate text-xs text-neutral-500 lg:inline" data-testid="shell-user">
              {user?.email}
            </span>
            <button
              onClick={logout}
              data-testid="shell-logout"
              className="hidden rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 sm:inline-block"
            >
              Log out
            </button>
            <button
              onClick={logout}
              aria-label="Log out"
              data-testid="shell-logout-mobile"
              className="rounded-lg border border-neutral-200 bg-white p-2 text-neutral-600 hover:bg-neutral-50 sm:hidden"
            >
              <LogOut className="h-4 w-4"/>
            </button>
          </div>
        </div>
      </header>

      <MobileDrawer
        open={open}
        onClose={() => setOpen(false)}
        nav={nav}
        isActive={isActive}
        onNavigate={navigate}
        user={user}
        logout={logout}
      />

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">{children}</main>
    </div>
  );
}
